// Usage events for SOMA Labs' shared PostHog project, like the other SOMA Labs products. A handful of meaningful
// events (app opened, message sent, reaction added, app command run, blooming started / app published, @Bloom asked),
// each with `product: "bloom"` and `channel: "prod"` on bloom.somalabs.dev ("dev" elsewhere).
//
// Privacy: no message text, names, emails, channel or server names, app names people chose, or URLs with ids in them
// (pages are reported as routes like "/s/:server/:channel"); no autocapture, no session recording, no pageview or
// referrer capture, no GeoIP, Do Not Track and Global Privacy Control respected. A person is a one-way hash of their
// account id (so PostHog can count returning people) with no properties set.
//
// Speed: PostHog's library is bundled with the app (no outside script) but loaded only after the page has loaded and
// the browser is idle, so it never touches cold start; events before then wait in a small queue. Off entirely when
// the server says so (DEV_MODE: local and test servers; /api/config `analytics`).
import type { PostHog } from "posthog-js/dist/module.slim.no-external";
import { getConfig } from "./upload.ts";

const TOKEN = "phc_BUUkwj9RybgAcwDuNtNnjqVH8kLAbMAgj3VShEFmkmkb";
const HOST = "https://us.i.posthog.com";

type Props = Record<string, string | number | boolean>;

let state: "waiting" | "loading" | "on" | "off" = "waiting";
let ph: PostHog | null = null;
let queue: [string, Props][] = [];
let person: string | null = null;

/** Records an event (dropped when analytics are off; queued until PostHog has loaded). Never pass content or names. */
export function track(event: string, props: Props = {}) {
  if (state === "off") return;
  if (ph) ph.capture(event, props);
  else if (queue.length < 50) queue.push([event, props]);
}

/** Who's signed in (null: nobody). Only a hash of the id ever leaves the browser. */
export function setAnalyticsUser(id: string | null) {
  if (id === person) return;
  const was = person;
  person = id;
  if (!ph) return;
  if (id) void identify(id);
  else if (was) ph.reset();
}

async function identify(id: string) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`bloom:${id}`));
  const hash = [...new Uint8Array(digest)].slice(0, 16).map((b) => b.toString(16).padStart(2, "0")).join("");
  if (person === id) ph?.identify(hash);
}

/** A page as a route, with every id and code taken out. */
export function route(path: string): string {
  const p = path.split("/").filter(Boolean);
  if (p[0] === "s") {
    if (p[2] === "settings") return `/s/:server/settings${p[3] && /^[a-z-]{2,20}$/.test(p[3]) ? `/${p[3]}` : ""}`;
    if (p[2] === "apps") return "/s/:server/apps";
    if (p[2] === "bloom") return "/s/:server/bloom/:thread";
    return p[1] ? (p[2] ? "/s/:server/:channel" : "/s/:server") : "/s";
  }
  if (p[0] === "invite") return "/invite/:code";
  return p[0] && /^[a-z-]{2,20}$/.test(p[0]) ? `/${p[0]}` : "/";
}

const DROP = ["$referrer", "$referring_domain", "$initial_referrer", "$initial_referring_domain", "$initial_current_url", "$initial_pathname", "$title", "$raw_user_agent"];

function scrub(props: Record<string, unknown> | undefined) {
  if (!props) return;
  for (const k of DROP) delete props[k];
  for (const k of ["$current_url", "$pathname"]) {
    if (typeof props[k] === "string") props[k] = k === "$pathname" ? route(props[k] as string) : `${location.origin}${route(new URL(props[k] as string, location.origin).pathname)}`;
  }
}

async function load() {
  if (state !== "waiting") return;
  state = "loading";
  const config = await getConfig().catch(() => null);
  const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  if (!config?.analytics || gpc || navigator.doNotTrack === "1") {
    state = "off";
    queue = [];
    return;
  }
  try {
    const { posthog } = await import("posthog-js/dist/module.slim.no-external");
    posthog.init(TOKEN, {
      api_host: HOST,
      defaults: "2026-05-30",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      rageclick: false,
      capture_dead_clicks: false,
      enable_heatmaps: false,
      capture_performance: false,
      capture_exceptions: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_product_tours: true,
      disable_conversations: true,
      disable_web_experiments: true,
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      save_referrer: false,
      respect_dnt: true,
      persistence: "localStorage",
      person_profiles: "identified_only",
      property_denylist: DROP,
      before_send: (cr) => {
        scrub(cr.properties);
        scrub(cr.$set);
        scrub(cr.$set_once);
        return cr;
      },
    });
    posthog.register({ product: "bloom", channel: location.hostname === "bloom.somalabs.dev" ? "prod" : "dev", $geoip_disable: true });
    ph = posthog;
    state = "on";
    if (person) await identify(person);
    const standalone = matchMedia("(display-mode: standalone)").matches;
    posthog.capture("app_opened", { standalone, phone: matchMedia("(max-width: 760px)").matches });
    for (const [event, props] of queue.splice(0)) posthog.capture(event, props);
  } catch {
    state = "off";
    queue = [];
  }
}

/** Starts PostHog once the page has loaded and the browser is idle (never during cold start). */
export function installAnalytics() {
  const later = () => {
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
    if (idle) idle(() => void load(), { timeout: 8000 });
    else setTimeout(() => void load(), 3000);
  };
  if (document.readyState === "complete") setTimeout(later, 1500);
  else addEventListener("load", () => setTimeout(later, 1500), { once: true });
}
